import React, { useState, useEffect } from 'react'

import ModalGeneric from 'containers/ModalManager/Modals/ModalGeneric'
import { InvalidCardNumber, Loading, MedicRequest, Success, SuccessRequestTreatment } from './steps'

import * as S from './styles'

import InputValidation from 'utils/InputValidation'
import { createMedicRequest, createMedicalReferral } from 'services/treatments'

import { history } from '../../routers/AppRouter'
import { Specialities } from 'settings/_specialitiesSettings'
import * as routes from 'settings/_routesSettings'
import { INELIGIBLE } from 'settings/_patientTreatmentStatus'

import { FieldsData, PlanSettingsData } from './types'
import { planGroupsSettings, MedicalRequestTypeEnum } from 'settings/_planGroupSettings'
import { updatePatientDefaultPlan } from 'services/updatePatientDefaultPlan'
import { useDispatch } from 'react-redux'
import { AUTH_UPDATE_DEFAULT_PLAN } from 'redux/actions/_actionTypes'

interface OnRequestTreatementProps {
  consultationType: 'PSIQ' | 'PSIC'
  planCode?: string
}

export interface RequestTreatmentModalProps {
  professionalRole: 'PSIQ' | 'PSIC';
  cardNumber: string;
  variant?: 'PROFESSIONAL_PROFILE' | 'DASHBOARD';
  isCareplus?: boolean;
  isCassi?: boolean;
  isCapitation?: boolean;
  daysToAuthorizeProcedure?: number;
  planGroupCode: string;
  planCode: string;
  userName: string;
  onRequestTreatement?: (data: OnRequestTreatementProps) => Promise<string>
  onClose: () => void;
  onSuccess?: () => void;
}

interface InputValidationData {
  success: boolean
  message: string
}

interface FileData {
  success: boolean
  message: string
  encodedFile: string
}

export const RequestTreatmentModal = ({
  professionalRole,
  isCareplus = false,
  isCassi = false,
  isCapitation = false,
  cardNumber,
  variant = 'DASHBOARD',
  daysToAuthorizeProcedure,
  planGroupCode,
  planCode,
  userName,
  onRequestTreatement,
  onSuccess = () => null,
  onClose
}: RequestTreatmentModalProps): JSX.Element => {

  const dispatch = useDispatch()

  const planSettingsForInit = planGroupsSettings[planGroupCode] as PlanSettingsData
  const isAuthorizationGuideForInit = planSettingsForInit?.medicalRequest?.type === MedicalRequestTypeEnum.AUTHORIZATION_GUIDE

  const [fields, setFields] = useState<FieldsData>({
    hasMedicRequest: isAuthorizationGuideForInit ? true : (!(isCassi || isCareplus || isCapitation) ? true : undefined),
    medicRequest: {
      isValid: false,
      message: '',
      placeholder: planSettingsForInit?.medicalRequest?.placeholder || 'Anexar pedido médico',
      value: '',
      displayValue: ''
    },
    ...(isAuthorizationGuideForInit && {
      authorizationGuideNumbers: {
        value: '',
        isValid: false,
        message: ''
      }
    }),
    cid: {
      value: undefined,
      isValid: false,
      message: undefined
    },
    crm: {
      value: undefined,
      isValid: false,
      message: undefined
    },
    uf: undefined
  })

  const [readyToSubmit, setReadyToSubmit] = useState(false)

  const planSettings = planGroupsSettings[planGroupCode] as PlanSettingsData
  const isAuthorizationGuide = planSettings?.medicalRequest?.type === MedicalRequestTypeEnum.AUTHORIZATION_GUIDE

  const isDashboardVariant = variant === 'DASHBOARD'

  const currentSpecialty = Specialities.find(specialty => specialty.code === professionalRole)

  const currentProfessionalRole = currentSpecialty.role as 'psicólogo' | 'psiquiatra'

  const plansWithNoNeedForMedicRequest = () => {
    const plans = [ isCassi, isCapitation ]

    return plans.some(plan => plan)
  }

  const STEPS = {
    LOADING: {
      NAME: 'LOADING',
      BUTTON_TEXT: '',
      BUTTON_ACTION: () => null
    },
    SUCCESS: {
      NAME: 'SUCCESS',
      CONTENT: isDashboardVariant ? `Pronto, agora é só escolher um ${currentProfessionalRole} e agendar a sua consulta!` : `Pronto, agora é só agendar a sua consulta com o ${currentProfessionalRole}!`,
      BUTTON_TEXT: isDashboardVariant ? `ESCOLHER UM ${currentProfessionalRole.toUpperCase()}` : 'ENTENDI',
      BUTTON_ACTION: () => {
        if (isDashboardVariant) return history.push(professionalRole === 'PSIC' ? routes.HOME_PSIC : routes.HOME_PSIQ)
        onClose()
      }
    },
    MEDIC_REQUEST: {
      NAME: 'MEDIC_REQUEST',
      BUTTON_TEXT: 'SOLICITAR',
      BUTTON_ACTION: async () => {
        if (fields.hasMedicRequest === true || plansWithNoNeedForMedicRequest()) {
          await handleCreateMedicRequest()
        } else {
          await handleSendProdedureRequest()
        }
      }
    },
    INVALID_CARD_NUMBER: {
      NAME: 'INVALID_CARD_NUMBER',
      BUTTON_TEXT: 'FECHAR',
      BUTTON_ACTION: () => onClose()
    },
    SUCCESS_REQUEST_TREATMENT: {
      NAME: 'SUCCESS_REQUEST_TREATMENT',
      BUTTON_TEXT: 'CONHECER PSICÓLOGOS',
      BUTTON_ACTION: async () => {
        dispatch({ type: AUTH_UPDATE_DEFAULT_PLAN, payload: { planCode: planCode } })
        await updatePatientDefaultPlan({
          planCode: planCode,
          username: userName
        })
        history.push(routes.HOME_PSIC)
      }
    }
  } as const

  const PROFESSIONAL_ROLES_DATA = {
    PSIQ: {
      name: 'psiquiatra',
      defaultStep: STEPS.LOADING.NAME,
      medicRequestNotRequired: true
    },
    PSIC: {
      name: 'psicólogo',
      defaultStep: isCapitation ? STEPS.LOADING.NAME : STEPS.MEDIC_REQUEST.NAME,
      medicRequestNotRequired: isCapitation
    }
  }

  const [step, setStep] = useState(PROFESSIONAL_ROLES_DATA[professionalRole].defaultStep as 'LOADING'| 'SUCCESS_REQUEST_TREATMENT' | 'INVALID_CARD_NUMBER' | 'MEDIC_REQUEST' | 'SUCCESS')

  const handleUpdateRadioValue = (value: boolean) => {
    setFields(prevState => ({
      ...prevState,
      hasMedicRequest: value,
      medicRequest: {
        isValid: false,
        message: '',
        placeholder: planSettings?.medicalRequest?.placeholder || 'Anexar pedido médico',
        value: '',
        displayValue: ''
      },
      ...(isAuthorizationGuide && value && {
        authorizationGuideNumbers: {
          value: '',
          isValid: false,
          message: ''
        }
      })
    }))
  }

  const handleUpdateMedicRequestFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const getFile = e.target.files
    const fileName = getFile[0] ? getFile[0].name : ''

    const validation = new InputValidation(getFile, 'imageOrPdf')

    const hasNoFile = getFile.length === 0
    const fileIsValid = await validation.validate() as FileData

    setFields(prevState => ({
      ...prevState,
      medicRequest: {
        ...prevState.medicRequest,
        value: hasNoFile ? '' : fileIsValid.encodedFile,
        displayValue: fileName,
        isValid: hasNoFile ? false : fileIsValid.success,
        message: hasNoFile ? '' : fileIsValid.message
      }
    }))
  }

  const handleCreateMedicRequest = async () => {
    try {
      setStep(STEPS.LOADING.NAME)

      if (isAuthorizationGuide) {
        // Usa o service de medical-referral para guia de autorização
        await createMedicalReferral({
          medicalReferral: fields.medicRequest.value,
          planCode: planCode,
          medicalReferralIdentifier: fields.authorizationGuideNumbers?.value || ''
        })
      } else {
        // Usa o service tradicional de medical-request
        await createMedicRequest({
          treatmentValidity: {
            medicalRequest : fields.medicRequest.value,
            cid: fields.cid.value,
            professionalCrmCode: fields.crm.value,
            professionalCrmUf: fields.uf
          },
          isCareplus,
          planCode
        })
      }

      setStep(STEPS.SUCCESS_REQUEST_TREATMENT.NAME)
      onSuccess()
    } catch(err) {
      console.log(err)
      setStep(STEPS.INVALID_CARD_NUMBER.NAME)
    }
  }

  const handleSendProdedureRequest = async () => {
    try {
      setStep(STEPS.LOADING.NAME)

      await createMedicRequest({ isCareplus, planCode })
      const successStep = isCapitation ? STEPS.SUCCESS_REQUEST_TREATMENT.NAME : STEPS.SUCCESS.NAME
      setStep(successStep)
      onSuccess()
    } catch (err) {
      console.log(err)
      setStep(STEPS.INVALID_CARD_NUMBER.NAME)
    }
  }

  const handleRequestTreatment = () => {
    if (professionalRole === 'PSIQ') {
      onRequestTreatement({
        consultationType: professionalRole,
        planCode
      })
        .then((status) => setStep(status === INELIGIBLE ? STEPS.INVALID_CARD_NUMBER.NAME : STEPS.SUCCESS.NAME))
        .catch(() => setStep(STEPS.INVALID_CARD_NUMBER.NAME))
    }
    if (professionalRole === 'PSIC') handleSendProdedureRequest()
  }

  const handleUpdateUfValue = (value: string) => {
    setFields(prevState => ({
      ...prevState,
      uf: value
    }))
  }

  const handleUpdateCidValue = (value: string) => {
    const validation = new InputValidation(value, 'cid').validate() as InputValidationData

    setFields(prevState => ({
      ...prevState,
      cid: {
        value,
        isValid: validation.success,
        message: validation.message,
      }
    }))
  }

  const handleUpdateCrmValue = (value: number) => {
    const validation = new InputValidation(value, 'integer').validate() as InputValidationData

    setFields(prevState => ({
      ...prevState,
      crm: {
        value,
        isValid: validation.success,
        message: validation.message,
      }
    }))
  }

  const handleUpdateAuthorizationGuideNumbers = (value: string | number) => {
    const validation = new InputValidation(value, 'integer').validate() as InputValidationData
    const isEmpty = !value || (typeof value === 'string' && value.trim().length === 0)
    const isValid = validation.success && !isEmpty
    const message = isEmpty ? 'Este campo é obrigatório' : (validation.success ? '' : validation.message)

    setFields(prevState => ({
      ...prevState,
      authorizationGuideNumbers: {
        value: String(value),
        isValid,
        message,
      }
    }))
  }

  useEffect(() => {
    if (PROFESSIONAL_ROLES_DATA[professionalRole].medicRequestNotRequired) handleRequestTreatment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  useEffect(() => {
    if (isAuthorizationGuide) {
      const isReady = (
        fields.medicRequest.isValid &&
        fields.authorizationGuideNumbers?.isValid === true
      )
      setReadyToSubmit(isReady)
    } else {
      const isReady = (
        (fields.cid.isValid && fields.crm.isValid && fields.medicRequest.isValid && fields.uf !== undefined)
        || (fields.medicRequest.isValid && !planSettings?.diseaseInfo && !planSettings?.doctorInfo)
        || fields.hasMedicRequest === false
      )
      setReadyToSubmit(isReady)
    }
  }, [
    fields.crm,
    fields.cid,
    fields.uf,
    fields.medicRequest,
    fields.authorizationGuideNumbers,
    fields.hasMedicRequest,
    planSettings,
    isAuthorizationGuide
  ])

  return (
    <ModalGeneric
      onCloseModal={onClose}
      modalTitle={step === 'MEDIC_REQUEST' && `Solicitar consulta com ${PROFESSIONAL_ROLES_DATA[professionalRole].name}`}
      btnOnClick={STEPS[step].BUTTON_ACTION}
      btnText={STEPS[step].BUTTON_TEXT}
      btnDisabled={!readyToSubmit && step === STEPS.MEDIC_REQUEST.NAME}
      hasButton={step !== 'LOADING'}
      hasCloseButton={step !== STEPS.LOADING.NAME}
    >
      <S.Container data-cy='request-treatment-modal'>
        {step === STEPS.MEDIC_REQUEST.NAME && (
          <MedicRequest
            showMedicRequestOption={isCassi || isCareplus || isCapitation}
            selectedOption={fields.hasMedicRequest}
            medicRequestIsValid={fields.medicRequest.isValid}
            inputPlaceholder={fields.medicRequest.placeholder}
            inputMessage={fields.medicRequest.message}
            inputDisplayValue={fields.medicRequest.displayValue}
            handleUpdateMedicRequestFile={handleUpdateMedicRequestFile}
            handleUpdateRadioValue={handleUpdateRadioValue}
            handleUpdateCrmValue={handleUpdateCrmValue}
            handleUpdateCidValue={handleUpdateCidValue}
            handleUpdateUfValue={handleUpdateUfValue}
            handleUpdateAuthorizationGuideNumbers={handleUpdateAuthorizationGuideNumbers}
            formFields={fields}
            planSettings={planSettings}
          />
        )}
        {step === STEPS.LOADING.NAME && <Loading data-cy='request-treatment-modal-loading' professionalRole={currentProfessionalRole} />}
        {step === STEPS.SUCCESS.NAME && <Success>{STEPS.SUCCESS.CONTENT}</Success>}
        {step === STEPS.INVALID_CARD_NUMBER.NAME && (
          <InvalidCardNumber
            cardNumber={cardNumber}
            customTitle={isAuthorizationGuide ? 'Por favor, tente novamente' : undefined}
            customMessage={isAuthorizationGuide
              ? 'Verifique o documento anexado e revise os números digitados para ter certeza de que eles correspondem aos da guia de autorização.'
              : undefined
            }
          />
        )}
        {step === STEPS.SUCCESS_REQUEST_TREATMENT.NAME && (
          <SuccessRequestTreatment
            daysToAuthorizeProcedure={daysToAuthorizeProcedure}
            isAuthorizationGuide={isAuthorizationGuide}
            professionalRoleText={currentProfessionalRole}
          />
        )}
      </S.Container>
    </ModalGeneric>
  )
}
